import { VOICE_GUIDED_FIELD_TYPE } from "./voiceGuidedFormTypes";

const EMAIL_PATTERN = /\b[\w.+-]+@[\w.-]+\.[a-z]{2,}\b/i;

const DIGIT_WORDS = Object.freeze({
  zero: "0",
  oh: "0",
  one: "1",
  two: "2",
  three: "3",
  four: "4",
  five: "5",
  six: "6",
  seven: "7",
  eight: "8",
  nine: "9",
});

const TENS_WORDS = Object.freeze({
  twenty: 20,
  thirty: 30,
  forty: 40,
  fifty: 50,
  sixty: 60,
  seventy: 70,
  eighty: 80,
  ninety: 90,
});

function convertCompoundNumberPhrases(text) {
  let result = text;

  for (const [tenWord, tenValue] of Object.entries(TENS_WORDS)) {
    for (const [digitWord, digitValue] of Object.entries(DIGIT_WORDS)) {
      if (digitValue === "0") {
        continue;
      }

      const pattern = new RegExp(`\\b${tenWord}\\s+${digitWord}\\b`, "gi");
      result = result.replace(pattern, String(tenValue + Number(digitValue)));
    }
  }

  return result;
}

function convertDigitWordSequences(text) {
  const digitWordPattern =
    /\b(?:(?:zero|oh|one|two|three|four|five|six|seven|eight|nine)(?:\s+(?:zero|oh|one|two|three|four|five|six|seven|eight|nine))+)\b/gi;

  return text.replace(digitWordPattern, (match) =>
    match
      .trim()
      .split(/\s+/)
      .map((word) => DIGIT_WORDS[word.toLowerCase()] ?? word)
      .join(""),
  );
}

/**
 * Normalize spoken email phrases before pattern matching.
 * "at" → "@", "dot" → ".", spoken numbers → digits, trim local/domain spaces.
 */
export function normalizeSpokenEmailTranscript(text) {
  let normalized = convertCompoundNumberPhrases(
    convertDigitWordSequences(String(text || "").toLowerCase().trim()),
  );

  normalized = normalized.replace(/\s+at\s+/gi, "@");
  normalized = normalized.replace(/\bdot\b/gi, ".");
  normalized = normalized.replace(/\s*@\s*/g, "@");
  normalized = normalized.replace(/\s*\.\s*/g, ".");

  if (!normalized.includes("@")) {
    return normalized;
  }

  const atIndex = normalized.indexOf("@");
  const local = normalized.slice(0, atIndex).replace(/\s+/g, "");
  const domain = normalized.slice(atIndex + 1).replace(/\s+/g, "");
  return `${local}@${domain}`;
}

export function extractEmailFromVoiceTranscript(text) {
  const prepared = normalizeSpokenEmailTranscript(text);
  if (!looksLikeEmail(prepared)) {
    return null;
  }

  return normalizeEmail(prepared);
}

export function looksLikeEmail(text) {
  const trimmed = String(text || "").trim();
  if (!trimmed) {
    return false;
  }

  return EMAIL_PATTERN.test(trimmed);
}

export function normalizeEmail(text) {
  const match = String(text || "").match(EMAIL_PATTERN);
  return match ? match[0].toLowerCase() : String(text || "").trim();
}

export function normalizePhone(text) {
  return String(text || "").replace(/\s+/g, " ").trim();
}

export function normalizeText(text) {
  return String(text || "").trim();
}

export function normalizeNumber(text) {
  const cleaned = String(text || "")
    .replace(/[^0-9.-]/g, "")
    .trim();
  return cleaned;
}

export function normalizeDate(text) {
  const trimmed = String(text || "").trim();

  const spokenDate = trimmed.match(
    /(\d{4})[-/](\d{1,2})[-/](\d{1,2})/,
  );
  if (spokenDate) {
    const [, year, month, day] = spokenDate;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }

  return trimmed;
}

export function normalizeDropdownMatchText(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function matchDropdownOption(transcript, options = []) {
  const normalized = normalizeDropdownMatchText(transcript);
  if (!normalized || !options.length) {
    return null;
  }

  const exact = options.find(
    (option) => normalizeDropdownMatchText(option.label) === normalized,
  );
  if (exact) {
    return exact.value;
  }

  let bestMatch = null;
  let bestLabelLength = 0;

  for (const option of options) {
    const label = normalizeDropdownMatchText(option.label);
    const isMatch =
      normalized.includes(label) || label.includes(normalized);

    if (isMatch && label.length > bestLabelLength) {
      bestMatch = option;
      bestLabelLength = label.length;
    }
  }

  return bestMatch?.value ?? null;
}

/**
 * Resolve a routed contact utterance to phone or email field + value.
 */
export function resolveRoutedContact(transcript, { emailFieldId, phoneFieldId }) {
  if (looksLikeEmail(transcript)) {
    return {
      fieldId: emailFieldId,
      value: normalizeEmail(transcript),
    };
  }

  return {
    fieldId: phoneFieldId,
    value: normalizePhone(transcript),
  };
}

/**
 * Apply field-type normalization for a guided form step.
 */
export function parseVoiceFieldValue(step, transcript, binding) {
  const fieldType = step.type || binding?.type || VOICE_GUIDED_FIELD_TYPE.TEXT;

  if (fieldType === VOICE_GUIDED_FIELD_TYPE.ROUTED_CONTACT) {
    return resolveRoutedContact(transcript, {
      emailFieldId: step.emailFieldId,
      phoneFieldId: step.phoneFieldId,
    });
  }

  if (fieldType === VOICE_GUIDED_FIELD_TYPE.DROPDOWN) {
    const matched = matchDropdownOption(transcript, binding?.options);
    return {
      fieldId: step.fieldId,
      value: matched ?? normalizeText(transcript),
    };
  }

  if (fieldType === VOICE_GUIDED_FIELD_TYPE.NUMBER) {
    return {
      fieldId: step.fieldId,
      value: normalizeNumber(transcript),
    };
  }

  if (fieldType === VOICE_GUIDED_FIELD_TYPE.DATE) {
    return {
      fieldId: step.fieldId,
      value: normalizeDate(transcript),
    };
  }

  return {
    fieldId: step.fieldId,
    value: normalizeText(transcript),
  };
}
